//  ==========================================================================
//
//  Search
//
//  ==========================================================================
import React from 'react';
import { graphql } from 'gatsby';
import { Provider } from 'react-redux';
import store from '../components/Search/store';
import { initialState } from '../components/Search/store/reducer';
import { getUrlFilters } from '../components/Search/utils';
import queryString from 'query-string';
import startCase from 'lodash/startCase';
import SearchMarkup from '../components/Search';

const contentTypesData = [
	{ post_title: 'Announcements', post_title_untranslated: 'Announcements', slug: 'announcements',},
	{ post_title: 'Awards', post_title_untranslated: 'Awards', slug: 'awards',},
	{ post_title: 'Case Studies', post_title_untranslated: 'Case Studies', slug: 'case-studies',},
	{ post_title: 'Events', post_title_untranslated: 'Events', slug: 'events',},
	{ post_title: 'News', post_title_untranslated: 'News', slug: 'news',},
	{ post_title: 'People', post_title_untranslated: 'People', slug: 'people',},
	{ post_title: 'Reports', post_title_untranslated: 'Reports', slug: 'reports',},
	{ post_title: 'Stories', post_title_untranslated: 'Stories', slug: 'stories',},
];

const Search = ( { data, location } ) => {

	// Create a dictionary keyed by slug to allow mapping to translated and unstranslated display text. This needs to be co-located with getFilterText
	const filterTranslationDict = {};
	contentTypesData.forEach( item => { filterTranslationDict[item.slug] = {post_title: item.post_title, post_title_untranslated: item.post_title_untranslated}; } );
	data?.locations?.nodes?.forEach( item => { filterTranslationDict[item.slug] = {post_title: item.post_title, post_title_untranslated: item.post_title_untranslated}; } );
	data?.projectTypes?.nodes?.forEach( item => { filterTranslationDict[item.slug] = {post_title: item.post_title, post_title_untranslated: item.post_title_untranslated}; } );
	data?.services?.nodes?.forEach( item => { filterTranslationDict[item.slug] = {post_title: item.post_title, post_title_untranslated: item.post_title_untranslated}; } );

	// Uses a term's slug to get the display text (translated OR untranslated) by the slug
	const getFilterText = ( slug, translated = true ) => {
		if ( Object.keys( filterTranslationDict ).includes( slug ) ) {
			return translated ? filterTranslationDict[slug].post_title : filterTranslationDict[slug].post_title_untranslated; 
		}
		return startCase( slug.replace( '-', ' ' ) );
	};

	const filters = {
		locations: {
			filterTitle: 'Locations',
			filterTitleUntranslated: 'Locations',
			filterTitleSlug: 'locations',
			data: data?.locations?.nodes?.map( 
				item => ( { post_title: item.post_title, post_title_untranslated: item.post_title_untranslated, slug: item.slug } )
			).sort(),
		},
		projectTypes: {
			filterTitle: 'Project Types',
			filterTitleUntranslated: 'Project Types',
			filterTitleSlug: 'project-types',
			data: data?.projectTypes?.nodes?.map( 
				item => ( { post_title: item.post_title, post_title_untranslated: item.post_title_untranslated, slug: item.slug } ) 
			).sort(),
		},
		services: {
			filterTitle: 'Services',
			filterTitleUntranslated: 'Services',
			filterTitleSlug: 'services',
			data: data?.services?.nodes?.map( 
				item => ( { post_title: item.post_title, post_title_untranslated: item.post_title_untranslated, slug: item.slug } ) 
			).sort(),
		},
		contentTypes: {
			filterTitle: 'Content Types',
			filterTitleUntranslated: 'Content Types',
			filterTitleSlug: 'content-types',
			data: contentTypesData,
		},
	};

	const searchValue = location.search || '';

	const STORE = store( {
		...initialState,
		description: data.pageMeta.edges[0].node.search_meta_desc,
		searchInputValue: queryString.parse( searchValue ).keywords || '',
		selectedFilters: getUrlFilters( location, filters ),
		filters,
	} );

	return (
		<Provider store={STORE}>
			<SearchMarkup location={location} getFilterText={getFilterText} />
		</Provider>
	);
};

export default Search;

export const searchQuery = graphql`
	query SearchQuery {
		locations: allWordsbyCollections(
			filter: {
				post_type: { eq: "location" }
				ID: { nin: [395, 338, 336, 366, 356, 404, 381, 375] }
				meta: { hide_from_site_search: { ne: true } }
			},
			sort: { fields: [post_title], order: ASC }
		) {
			nodes {
				post_title
				post_title_untranslated: post_title
				slug: post_name
			}
		}

		projectTypes: allWordsbyCollections(
			filter: {
				post_type: { eq: "project_type" }
				ID: { nin: [395, 338, 336, 366, 356, 404, 381, 375] }
				meta: { hide_from_site_search: { ne: true } }
			},
			sort: { fields: [post_title], order: ASC }
		) {
			nodes {
				post_title
				post_title_untranslated: post_title
				slug: post_name
			}
		}

		services: allWordsbyCollections(
			filter: {
				post_type: { eq: "service" }
				ID: { nin: [395, 338, 336, 366, 356, 404, 381, 375] }
				meta: { hide_from_site_search: { ne: true } }
			},
			sort: { fields: [post_title], order: ASC }
		) {
			nodes {
				post_title
				post_title_untranslated: post_title
				slug: post_name
			}
		}

		pageMeta: allWordsbyCollections(
			filter: { post_type: { eq: "static_page_meta" } }
		) {
			edges {
				node {
					search_meta_desc
				}
			}
		}
	}
`;
